import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowRight,
  Check,
  FileUp,
  Sparkles,
  Upload,
  Wand2,
} from "lucide-react";

type ImpactAnalysisModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onChooseManual: () => void;
  onChooseUpload: () => void;
};

export default function ImpactAnalysisModal({
  isOpen,
  onClose,
  onChooseManual,
  onChooseUpload,
}: ImpactAnalysisModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] p-4 md:p-8">
        <DialogHeader className="text-center mb-2">
          <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-3">
            <Sparkles className="w-7 h-7 text-blue-600" />
          </div>
          <DialogTitle className="text-xl md:text-2xl font-bold text-slate-800 text-center">
            Cum dorești să completezi analiza de impact?
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Alege metoda care ți se potrivește. Poți oricând reveni și schimba.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={onChooseManual}
            className="relative flex flex-col items-center text-center border-2 border-blue-500 rounded-xl p-5 md:p-6 gap-3 bg-blue-50/30 shadow-md hover:shadow-lg hover:scale-[1.02] hover:border-blue-600 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
            style={{ animationDelay: "100ms", animationDuration: "400ms" }}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
              RECOMANDAT
            </div>

            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md">
              <Wand2 className="w-7 h-7 text-white" />
            </div>

            <h3 className="font-bold text-slate-800 text-base">
              Completare manuală
            </h3>

            <p className="text-xs text-slate-500 leading-relaxed">
              Completează analiza efect perturbator pas cu pas, conform Art. 9
              din OUG 155/2024.
            </p>

            <div className="space-y-1.5 text-left w-full">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <span>Evaluare impactului perturbator</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <span>Generare raport PDF</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <span>Salvare și editare ulterioară</span>
              </div>
            </div>

            <div className="w-full pt-2 mt-auto">
              <div className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-button">
                Începe analiza
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </button>

          <button
            onClick={onChooseUpload}
            className="flex flex-col items-center text-center border border-slate-200 rounded-xl p-5 md:p-6 gap-3 bg-white hover:shadow-md hover:scale-[1.02] hover:border-slate-300 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
            style={{ animationDelay: "200ms", animationDuration: "400ms" }}
          >
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
              <FileUp className="w-7 h-7 text-emerald-600" />
            </div>

            <h3 className="font-semibold text-slate-700 text-base">
              Încarcă Formular XLSX
            </h3>

            <p className="text-xs text-slate-500 leading-relaxed">
              Ai deja analiza de impact completată? Extrage automat datele din
              fișierul XLSX.
            </p>

            <div className="space-y-1.5 text-left w-full">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Extragere automată din XLSX</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Verificare și ajustare ulterioară</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Salvare directă sau editare</span>
              </div>
            </div>

            <div className="w-full pt-2 mt-auto">
              <div className="w-full py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium flex items-center justify-center gap-2">
                Încarcă document
                <Upload className="w-4 h-4" />
              </div>
            </div>
          </button>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4">
          Poți schimba metoda oricând. Datele se salvează automat.
        </p>
      </DialogContent>
    </Dialog>
  );
}
